import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import DonutChartFrame from './DonutChartFrame';

const ORG_COLORS = {
  Partner: '#3b7de0',
  VSE: '#7f52db',
  'Customer Success': '#11a5c9',
  PAE: '#ef9b0f',
};

const ORG_TINT_CLASS = {
  Partner: 'bootcamp-org-tint-partner',
  VSE: 'bootcamp-org-tint-vse',
  'Customer Success': 'bootcamp-org-tint-cs',
  PAE: 'bootcamp-org-tint-pae',
};

const getOrgColor = (org) => ORG_COLORS[org] || '#657893';

const getOrgTintClass = (org) => ORG_TINT_CLASS[org] || 'bootcamp-org-tint-default';

const getCompletionToneClass = (value) => {
  if (value >= 33) {
    return 'bootcamp-tone-strong';
  }
  if (value >= 26) {
    return 'bootcamp-tone-mid';
  }
  return 'bootcamp-tone-low';
};

const OrganizationBreakdownCard = ({
  organizationBreakdown = [],
  exportId = 'export-organization-breakdown',
}) => {
  const totalParticipants = useMemo(
    () => organizationBreakdown.reduce((sum, item) => sum + (item.totalParticipants || 0), 0),
    [organizationBreakdown]
  );

  const isCompact = organizationBreakdown.length >= 4;

  const organizationDonutOption = useMemo(
    () => ({
      tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
      legend: { show: false },
      series: [
        {
          type: 'pie',
          radius: ['58%', '78%'],
          center: ['50%', '50%'],
          label: { show: false },
          labelLine: { show: false },
          itemStyle: {
            borderColor: '#ffffff',
            borderWidth: 2,
          },
          data: organizationBreakdown.map((item) => ({
            name: item.org,
            value: item.totalParticipants,
            itemStyle: {
              color: getOrgColor(item.org),
            },
          })),
        },
      ],
    }),
    [organizationBreakdown]
  );

  const donutHeight = isCompact ? 200 : 220;

  return (
    <section id={exportId} className="bootcamp-card bootcamp-org-breakdown-card">
      <h3>Breakdown by Organization Type</h3>

      <div className="bootcamp-org-breakdown-layout">
        <div className="bootcamp-org-donut-panel">
          <div className="bootcamp-org-donut-wrap">
            <DonutChartFrame
              center={['50%', '50%']}
              centerLabel={(
                <>
                  <strong>{totalParticipants}</strong>
                  <span>Total</span>
                </>
              )}
              centerClassName="bootcamp-org-donut-center"
              height={`${donutHeight}px`}
            >
              <ReactECharts
                option={organizationDonutOption}
                style={{ width: '100%', height: donutHeight }}
              />
            </DonutChartFrame>
          </div>

          <div className="bootcamp-org-donut-legend">
            {organizationBreakdown.map((item) => (
              <span className="bootcamp-org-donut-legend-item" key={item.org}>
                <i
                  className="bootcamp-dot"
                  style={{ backgroundColor: getOrgColor(item.org) }}
                />
                {item.org}
              </span>
            ))}
          </div>
        </div>

        <div className={`bootcamp-org-summary-list ${isCompact ? 'is-compact' : ''}`}>
          {organizationBreakdown.map((item) => (
            <article
              className={`bootcamp-org-summary-card ${getOrgTintClass(item.org)} ${
                item.org === 'PAE' ? 'is-pae' : ''
              } ${isCompact ? 'is-compact' : ''}`}
              key={item.org}
            >
              <div className="bootcamp-org-summary-card-head">
                <div className="bootcamp-org-summary-title">
                  <i
                    className="bootcamp-dot bootcamp-org-summary-dot"
                    style={{ backgroundColor: getOrgColor(item.org) }}
                  />
                  <strong>{item.org}</strong>
                </div>
                <span
                  className={`bootcamp-org-rate-badge ${getCompletionToneClass(item.completionRate)}`}
                >
                  {item.completionRate}%
                </span>
              </div>

              <div className="bootcamp-org-summary-metrics">
                <span className="bootcamp-org-metric">
                  <span className="bootcamp-org-metric-label">Total</span>
                  <span className="bootcamp-org-metric-value bootcamp-value-total">
                    {item.totalParticipants}
                  </span>
                </span>
                <span className="bootcamp-org-metric">
                  <span className="bootcamp-org-metric-label">Certified</span>
                  <span className="bootcamp-org-metric-value bootcamp-value-certified">
                    {item.certified}
                  </span>
                </span>
                <span className="bootcamp-org-metric">
                  <span className="bootcamp-org-metric-label">Pending</span>
                  <span className="bootcamp-org-metric-value bootcamp-value-pending">
                    {item.pendingAction}
                  </span>
                </span>
              </div>

              <div className="bootcamp-org-progress-bar">
                <span
                  className="bootcamp-org-progress-fill"
                  style={{
                    width: `${Math.min(100, Math.max(0, item.completionRate || 0))}%`,
                    backgroundColor: getOrgColor(item.org),
                  }}
                />
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default OrganizationBreakdownCard;
